<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import TextCheckOptions from './TextCheckOptions.vue';
import InterviewStrategyChangeModalWarning from './InterviewStrategyChangeModalWarning.vue';
import { FeatureName, PolicyEnforcement } from '@factoryfixinc/ats-interfaces';
import PolicyTooltip from './PolicyTooltip.vue';
import UpgradeToGoldModal from '@/components/Shared/Modals/UpgradeToGoldModal.vue';
import { UpgradeToGoldVariant } from '@/core/shared/dialog/dialog.types';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import GoldUpgradeConfirmedModal from '@/components/Shared/Modals/GoldUpgradeConfirmedModal.vue';
import UnlockedLockSvg from '@/assets/svg/unlocked-lock-20.svg?component';

const emit = defineEmits<{
  (e: 'change-enabled', value: boolean): void;
}>();

const dialogService = new DialogService();

const featureGatingService = new EmployerFeatureGatingService();

const props = defineProps<{
  enabled: boolean;
  interviewSchedulingPolicy?: PolicyEnforcement;
}>();

const isEnabled = computed(() => props.enabled);
const showWarningModal = ref(false);
const isModalLoading = ref(false);
const hasShownWarningModal = ref(false);

const hasInterviewSchedulingAccess = computed(() =>
  featureGatingService.hasAccess(FeatureName.INTERVIEW_SCHEDULING),
);

const isRequiredPolicy = computed(
  () => props.interviewSchedulingPolicy === PolicyEnforcement.REQUIRED,
);
const isForbiddenPolicy = computed(
  () => props.interviewSchedulingPolicy === PolicyEnforcement.FORBIDDEN,
);

onMounted(() => {
  if (!hasInterviewSchedulingAccess.value) {
    emit('change-enabled', false);
  }
});

function setEnabled(value: boolean) {
  if (value && !hasInterviewSchedulingAccess.value) {
    dialogService.openUpgradeToGoldModal(UpgradeToGoldVariant.InterviewScheduling);
    return;
  }

  if (value && isForbiddenPolicy.value) {
    return;
  }

  if (!value && isRequiredPolicy.value) {
    return;
  }

  if (props.enabled && !value && !hasShownWarningModal.value) {
    hasShownWarningModal.value = true;
    showWarningModal.value = true;
    return;
  }

  emit('change-enabled', value);
}

function handleWarningConfirm() {
  emit('change-enabled', false);
  showWarningModal.value = false;
}

function handleWarningCancel() {
  showWarningModal.value = false;
}
</script>

<template>
  <div>
    <h3 class="mb-4 font-bold text-black">
      Do you want your AI recruiter to schedule interviews for you?
    </h3>

    <div class="flex gap-3">
      <TextCheckOptions
        :is-selected="!isEnabled && !isRequiredPolicy"
        :is-disabled="isRequiredPolicy"
        @click="setEnabled(false)"
        class="flex-1"
      >
        <template #title>
          <span class="flex items-center justify-between">
            No, I'll review first
            <PolicyTooltip
              v-if="isRequiredPolicy"
              :feature-policy="interviewSchedulingPolicy"
              feature="No, I'll review first"
            />
          </span>
        </template>
        You'll review each candidate first and decide how to proceed from there
      </TextCheckOptions>

      <TextCheckOptions
        :is-selected="isEnabled && !isForbiddenPolicy"
        :is-disabled="isForbiddenPolicy"
        @click="setEnabled(true)"
        class="flex-1"
      >
        <template #header v-if="!hasInterviewSchedulingAccess">
          <div class="flex justify-between">
            <span class="rounded-full bg-gold px-2 text-[10px] font-bold text-shade-900">
              Access with FactoryFix Gold
            </span>
            <UnlockedLockSvg class="h-5 w-5" />
          </div>
        </template>
        <template #title>
          <span class="flex items-center justify-between">
            Yes, schedule interviews
            <PolicyTooltip
              v-if="isForbiddenPolicy"
              :feature-policy="interviewSchedulingPolicy"
              feature="Yes, schedule interviews"
            />
          </span>
        </template>
        Your AI recruiter will book interviews with high-scoring candidates who complete screening
      </TextCheckOptions>
    </div>

    <InterviewStrategyChangeModalWarning
      :is-open="showWarningModal"
      :is-loading="isModalLoading"
      @confirm="handleWarningConfirm"
      @cancel="handleWarningCancel"
    />

    <!-- Upgrade to Gold Modals required here since the job page uses a different layout -->
    <UpgradeToGoldModal />
    <GoldUpgradeConfirmedModal />
  </div>
</template>
